import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import DeleteIcon from "@mui/icons-material/Delete";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { Formik, FieldArray } from "formik";
import * as yup from "yup";
import Form from "react-bootstrap/Form";
import { useDispatch } from "react-redux";

import ModalAllder from "components/layout/ModalAllder";
import { fetchApiData } from "../../../../utils/apiUtils";
import { memberArInvoiceHandler } from "../../../../redux/handlers";
import { ENDPOINT } from "../../../../constants/server/api";

// ─── Formatter ───────────────────────────────────────────────
function fmt(v) {
  if (v == null || v === "" || isNaN(Number(v))) return "0.00";
  return new Intl.NumberFormat("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(v));
}

// ─── Validation schema ───────────────────────────────────────
const schema = yup.object().shape({
  payment_date: yup.string().required("กรุณาระบุวันที่รับชำระ"),

  // Section 1 — รายการชำระเงิน
  splits: yup.array().when("isShowSplits", {
    is: true,
    then: (s) =>
      s.of(
        yup.object().shape({
          channel: yup
            .string()
            .required("กรุณาเลือกช่องทางชำระเงิน"),
          amount: yup
            .number()
            .required("กรุณาระบุจำนวนเงิน")
            .min(0.01, "จำนวนเงินต้องมากกว่า 0"),
        }),
      ),
    otherwise: (s) => s.notRequired(),
  }),

  // Section 2 — ค่าธรรมเนียม / รายการปรับปรุง
  fee_adjustments: yup.array().when("isShowFee", {
    is: true,
    then: (s) =>
      s.of(
        yup.object().shape({
          ar_adjustment_type_id: yup
            .number()
            .required("กรุณาเลือกประเภทปรับปรุง")
            .min(1, "กรุณาเลือกประเภทปรับปรุง"),
          amount: yup
            .number()
            .required("กรุณาระบุจำนวนเงิน")
            .notOneOf([0], "จำนวนเงินต้องไม่เป็น 0"),
        }),
      ),
    otherwise: (s) => s.notRequired(),
  }),

  // Section 3 — ตัดชำระเอกสาร
  document_offsets: yup.array().when("isShowDocument", {
    is: true,
    then: (s) =>
      s.of(
        yup.object().shape({
          offset_document_type: yup.string().required("กรุณาเลือกประเภทเอกสาร"),
          amount_applied: yup
            .number()
            .required("กรุณาระบุจำนวนที่รับชำระ")
            .min(0.01, "จำนวนเงินต้องมากกว่า 0"),
        }),
      ),
    otherwise: (s) => s.notRequired(),
  }),

  // Section 4 — ถูกหัก ณ ที่จ่าย
  ar_withholding_tax_rate_id: yup.mixed().when("isShowVat", {
    is: true,
    then: (s) =>
      s
        .required("กรุณาเลือกอัตราภาษีหัก ณ ที่จ่าย")
        .test("not-empty", "กรุณาเลือกอัตราภาษีหัก ณ ที่จ่าย", (v) => !!v),
    otherwise: (s) => s.notRequired(),
  }),
});

// ─── Initial values factory ──────────────────────────────────
function buildInitialValues(editData) {
  if (editData) {
    return {
      payment_date: editData.completed_at
        ? dayjs(editData.completed_at).toISOString()
        : dayjs().toISOString(),
      isShowSplits: (editData.splits ?? []).length > 0,
      splits: (editData.splits ?? []).map((s) => ({
        // reverse-map: ถ้ามี branch_bank_setting_id → TRANSFER, ไม่งั้น → CASH
        channel: s.branch_bank_setting_id ? "TRANSFER" : "CASH",
        amount: Number(s.amount) ?? 0,
        remark: s.remark ?? "",
      })),
      isShowFee: (editData.feeAdjustments ?? []).length > 0,
      fee_adjustments: (editData.feeAdjustments ?? []).map((f) => ({
        ar_adjustment_type_id: f.ar_adjustment_type_id ?? "",
        ar_adjustment_item_id: f.ar_adjustment_item_id ?? "",
        amount: Number(f.amount) ?? 0,
        remark: f.remark ?? "",
      })),
      isShowDocument: (editData.documentOffsets ?? []).length > 0,
      document_offsets: (editData.documentOffsets ?? []).map((d) => ({
        offset_document_type: d.offset_document_type ?? "",
        reference_document_no: d.reference_document_no ?? "",
        amount_applied: Number(d.amount_applied) ?? 0,
        remark: d.remark ?? "",
      })),
      isShowVat: !!editData.ar_withholding_tax_rate_id,
      ar_withholding_tax_rate_id: editData.ar_withholding_tax_rate_id ?? "",
      withholding_tax_rate: Number(editData.withholding_tax_rate) ?? 0,
    };
  }
  return {
    payment_date: dayjs().toISOString(),
    isShowSplits: true,
    splits: [{ channel: "", amount: 0, remark: "" }],
    isShowFee: false,
    fee_adjustments: [
      {
        ar_adjustment_type_id: "",
        ar_adjustment_item_id: "",
        amount: 0,
        remark: "",
      },
    ],
    isShowDocument: false,
    document_offsets: [
      {
        offset_document_type: "",
        reference_document_no: "",
        amount_applied: 0,
        remark: "",
      },
    ],
    isShowVat: false,
    ar_withholding_tax_rate_id: "",
    withholding_tax_rate: 0,
  };
}

// ─── Main Component ──────────────────────────────────────────
export default function ModalPayment({
  modal,
  setModal,
  invoice,
  businessSlug,
  ubgProfile,
  memberData,
  onSuccess,
  onAddPayment, // NEW: deferred create — called instead of API in create mode
  mode = "create", // 'create' | 'edit'
  editData = null, // receipt invoice object when editing
  pendingCount = 0, // NEW: number of already-pending (unsaved) payments
  pendingAppliedAmount = 0, // NEW: sum of pending net_paid_amount (excl. current edit)
  editingPendingReceiptNo = null, // เลขครั้งที่เมื่อแก้ไขรายการรอยืนยัน (ไม่นับเป็นครั้งใหม่)
}) {
  const dispatch = useDispatch();

  const [saving, setSaving] = useState(false);
  const [cashMethodId, setCashMethodId] = useState(null);
  const [transferMethodId, setTransferMethodId] = useState(null);
  const [adjustmentTypes, setAdjustmentTypes] = useState([]);
  const [adjustmentItems, setAdjustmentItems] = useState([]);
  const [whtRates, setWhtRates] = useState([]);

  const paidCount = invoice?.paymentReceiptInvoices?.length ?? 0;
  const isEditingPending = editingPendingReceiptNo != null;
  // รวม pending payments ที่ยังไม่ได้ยืนยันด้วย เพื่อแสดงเลขครั้งที่ถูกต้อง
  const receiptNo =
    editingPendingReceiptNo ??
    paidCount + pendingCount + (mode === "create" ? 1 : 0);

  // ─── Load master data ─────────────────────────────────────
  useEffect(() => {
    if (!modal?.open) return;

    const loadMasters = async () => {
      const [pm, at, ai, wht] = await Promise.all([
        fetchApiData({
          method: "get",
          endpoint: `${ENDPOINT.MASTER.PAYMENT_METHOD}/list`,
          dispatch,
        }),
        fetchApiData({
          method: "get",
          endpoint: `${ENDPOINT.MASTER.AR_ADJUSTMENT_TYPE}/list`,
          dispatch,
        }),
        fetchApiData({
          method: "get",
          endpoint: `${ENDPOINT.MASTER.AR_ADJUSTMENT_ITEM}/list`,
          dispatch,
        }),
        fetchApiData({
          method: "get",
          endpoint: `${ENDPOINT.MASTER.AR_WITHHOLDING_TAX_RATE}/list`,
          dispatch,
        }),
      ]);
      if (pm) {
        // เก็บเฉพาะ id ของ CASH และ BANK_TRANSFER เพื่อส่งไป API
        const cash = pm.find((p) => p.code === "CASH");
        const transfer = pm.find((p) => p.code === "BANK_TRANSFER");
        if (cash) setCashMethodId(cash.id);
        if (transfer) setTransferMethodId(transfer.id);
      }
      if (at) setAdjustmentTypes(at);
      if (ai) setAdjustmentItems(ai);
      if (wht) setWhtRates(wht);
    };

    loadMasters();
  }, [modal?.open]);

  // ─── Calculate totals from form values ───────────────────
  function calcTotals(values) {
    const splitsTotal = values.isShowSplits
      ? values.splits.reduce((sum, s) => sum + (Number(s.amount) || 0), 0)
      : 0;

    let adjustedTotal = 0;
    if (values.isShowFee) {
      values.fee_adjustments.forEach((f) => {
        const type = adjustmentTypes.find(
          (t) => t.id === Number(f.ar_adjustment_type_id),
        );
        const dir = type?.direction_value ?? 1;
        adjustedTotal += dir * (Number(f.amount) || 0);
      });
    }

    const documentOffsetsTotal = values.isShowDocument
      ? values.document_offsets.reduce(
          (sum, d) => sum + (Number(d.amount_applied) || 0),
          0,
        )
      : 0;

    // ─── WHT คำนวณแบบ ratio-based (สูตรจาก designer) ───────────
    // ratio     = (splits + fees) / invoice.grand_total
    // taxable   = (grand_total - vat_amount) × ratio  ← สินค้าก่อนภาษีตามสัดส่วนที่รับชำระ
    // whtAmount = taxable × whtRate
    let whtAmount = 0;
    if (values.isShowVat && values.ar_withholding_tax_rate_id) {
      const invoiceGrandTotal = Number(invoice?.grand_total) || 0;
      const invoiceVatAmount = Number(invoice?.vat_amount) || 0;
      const preVatTotal = invoiceGrandTotal - invoiceVatAmount; // มูลค่าก่อนภาษีรวม

      const totalReceived = splitsTotal + adjustedTotal; // Section 1 + Section 2
      const ratio =
        invoiceGrandTotal > 0 ? totalReceived / invoiceGrandTotal : 0;
      const taxable = preVatTotal * ratio;
      const rate = Number(values.withholding_tax_rate) || 0;
      whtAmount = taxable * rate;
    }

    // netPaid = ยอดที่ตัดหนี้ AR (WHT ไม่หัก — pardesco pattern)
    const netPaid = splitsTotal + adjustedTotal + documentOffsetsTotal;

    // cashReceived = เงินสดที่รับจริง (netPaid - wht)
    const cashReceived = netPaid - whtAmount;

    return {
      splitsTotal,
      adjustedTotal,
      documentOffsetsTotal,
      whtAmount,
      netPaid,
      cashReceived,
    };
  }

  // ─── Submit ───────────────────────────────────────────────
  const handleSubmit = async (values) => {
    if (!invoice?.id) return;

    const {
      splitsTotal,
      adjustedTotal,
      documentOffsetsTotal,
      whtAmount,
      netPaid,
      cashReceived,
    } = calcTotals(values);

    const body = {
      payment_date: values.payment_date,
      amount_applied: netPaid,
      lines_subtotal: splitsTotal,
      grand_total: splitsTotal,
      splits: values.isShowSplits
        ? values.splits.map((s, i) => ({
            payment_method_id:
              s.channel === "CASH"
                ? cashMethodId
                : transferMethodId,
            branch_bank_setting_id:
              s.channel === "TRANSFER"
                ? (invoice?.business?.settingBranchBankSetting?.id ?? null)
                : null,
            amount: Number(s.amount),
            sort_order: i + 1,
            remark: s.remark || null,
          }))
        : [],
      // Section 2
      fee_adjustments: values.isShowFee
        ? values.fee_adjustments
            .filter((f) => f.ar_adjustment_type_id && Number(f.amount) !== 0)
            .map((f, i) => ({
              ar_adjustment_type_id: Number(f.ar_adjustment_type_id),
              ar_adjustment_item_id: f.ar_adjustment_item_id
                ? Number(f.ar_adjustment_item_id)
                : null,
              amount: Number(f.amount),
              sort_order: i + 1,
              remark: f.remark || null,
            }))
        : [],
      // Section 3
      document_offsets: values.isShowDocument
        ? values.document_offsets
            .filter((d) => d.offset_document_type)
            .map((d, i) => ({
              offset_document_type: d.offset_document_type,
              reference_document_no: d.reference_document_no || null,
              amount_applied: Number(d.amount_applied),
              sort_order: i + 1,
              remark: d.remark || null,
            }))
        : [],
      // Section 4
      ar_withholding_tax_rate_id:
        values.isShowVat && values.ar_withholding_tax_rate_id
          ? Number(values.ar_withholding_tax_rate_id)
          : null,
      withholding_tax_rate: values.isShowVat
        ? Number(values.withholding_tax_rate)
        : 0,
      withholding_tax_amount: whtAmount,
      total_adjusted_amount: adjustedTotal,
      net_paid_amount: netPaid,
      // meta
      ...(mode === "create" && {
        business_slug: businessSlug,
        branch_id: invoice.branch_id,
        member_id: invoice.member_id,
        created_by_user_business_group_id: ubgProfile?.id,
      }),
    };

    // ─── Create mode: เก็บไว้ใน parent state (deferred) ─────
    if (mode === "create") {
      // enrich splits ด้วยชื่อ payment method เพื่อแสดงใน PendingPaymentCard
      // paymentMethodName เก็บใน local state เท่านั้น ไม่ส่งไป API
      const bankSetting = invoice?.business?.settingBranchBankSetting;
      const enrichedBody = {
        ...body,
        splits: body.splits.map((s, i) => {
          const ch = values.splits[i]?.channel;
          if (ch === "TRANSFER") {
            return {
              ...s,
              paymentMethodName: "โอนเงิน",
              _bankName: bankSetting?.bank?.name ?? null,
              _bankBranchName: bankSetting?.branch_name ?? null,
              _bankAccountNumber: bankSetting?.account_number ?? null,
              _bankAccountName: bankSetting?.account_name ?? null,
              _bankImagePath: bankSetting?.bank?.image_path ?? null,
            };
          }
          return { ...s, paymentMethodName: "เงินสด" };
        }),
      };
      onAddPayment?.(enrichedBody);
      // หมายเหตุ: handleConfirmPayments จะ strip paymentMethodName ออกก่อนส่ง API
      return;
    }

    // ─── Edit mode: call API ทันที ───────────────────────────
    setSaving(true);
    try {
      const result = await dispatch(
        memberArInvoiceHandler.updateMemberArPayment(
          invoice.id,
          editData.id,
          body,
        ),
      );
      if (result) {
        onSuccess?.(result);
      }
    } catch (err) {
      console.error("Payment error:", err);
    } finally {
      setSaving(false);
    }
  };

  const offsetDocumentTypeOptions = [
    { value: "CREDIT_NOTE", label: "ใบลดหนี้" },
    { value: "ADVANCE_PAYMENT", label: "เงินล่วงหน้า" },
    { value: "OVERPAYMENT", label: "ชำระเกิน" },
  ];

  return (
    <ModalAllder
      modal={modal}
      setModal={setModal}
      htitle={"การรับชำระเงิน"}
      hbtn={true}
    >
      <Formik
        initialValues={buildInitialValues(editData)}
        validationSchema={schema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ handleSubmit, handleChange, errors, values, setFieldValue }) => {
          const {
            splitsTotal,
            adjustedTotal,
            documentOffsetsTotal,
            whtAmount,
            netPaid,
            cashReceived,
          } = calcTotals(values);

          // ─── Channel options — build จาก business bank setting ───
          const bankSetting = invoice?.business?.settingBranchBankSetting;
          const bankDisplayName = bankSetting
            ? [
                bankSetting.bank?.name,
                bankSetting.branch?.branch_name
                  ? `(${bankSetting.branch.branch_name})`
                  : null,
              ]
                .filter(Boolean)
                .join(" ")
            : null;
          const grandTotal = invoice ? Number(invoice.grand_total) : 0;
          const paidAmount = invoice ? Number(invoice.paid_amount) : 0;
          // ยอดสูงสุดที่รับชำระได้ครั้งนี้ (ก่อนหักยอด splits ปัจจุบัน)
          const maxAvailable =
            mode === "create"
              ? grandTotal - paidAmount - pendingAppliedAmount
              : grandTotal -
                (paidAmount -
                  (editData ? Number(editData.amount_applied) : 0));
          const remaining = maxAvailable - splitsTotal;

          // onBlur handler — ถ้ายอดรวม splits เกิน maxAvailable ให้ปัด field นั้นลง
          const handleSplitAmountBlur = (index) => {
            if (!values.isShowSplits) return;
            const currentTotal = values.splits.reduce(
              (sum, s) => sum + (Number(s.amount) || 0),
              0,
            );
            if (currentTotal > maxAvailable) {
              const excess = currentTotal - maxAvailable;
              const currentVal = Number(values.splits[index].amount) || 0;
              const newVal = Math.max(0, currentVal - excess);
              setFieldValue(`splits[${index}].amount`, newVal);
            }
          };

          return (
            <Form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              {/* Header */}
              <div className="flex items-center flex-wrap gap-3 mb-2">
                <div className="text-lg font-bold">
                  รับชำระ {invoice?.document_no ?? "-"}
                </div>
                <div className="bg-[#E5F8ED] text-[#2DC76D] rounded-lg px-3 py-1 text-sm">
                  {mode === "edit" || isEditingPending
                    ? "แก้ไขการรับชำระ"
                    : `รับชำระครั้งที่ ${receiptNo}`}
                </div>
                <div className="ml-auto">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="วันที่รับชำระ"
                      format="DD/MM/YYYY"
                      value={
                        values.payment_date ? dayjs(values.payment_date) : null
                      }
                      onChange={(v) =>
                        setFieldValue("payment_date", v ? v.toISOString() : "")
                      }
                      slotProps={{
                        textField: {
                          size: "small",
                          error: !!errors.payment_date,
                          helperText: errors.payment_date,
                          required: true,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </div>

              <hr className="my-3" />

              {/* ── Section 1: รายการชำระเงิน ─────────────────── */}
              <div className="flex items-center mb-2">
                <div className="font-semibold text-base text-[#2DC76D] w-[240px]">
                  รายการชำระเงิน
                </div>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!values.isShowSplits}
                      onChange={(e) =>
                        setFieldValue("isShowSplits", e.target.checked)
                      }
                    />
                  }
                  label=""
                />
              </div>
              {values.isShowSplits && (
                <FieldArray name="splits">
                  {(arrayHelpers) => (
                    <div className="py-2">
                      {values.splits.map((split, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-7 gap-3 mb-3"
                        >
                          <div className="col-span-3">
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="ช่องทางรับชำระเงิน"
                              name={`splits[${index}].channel`}
                              value={split.channel}
                              onChange={handleChange}
                              error={errors.splits?.[index]?.channel != null}
                              helperText={errors.splits?.[index]?.channel}
                              required
                              SelectProps={{
                                renderValue: (val) => {
                                  if (val === "CASH") return "เงินสด";
                                  if (val === "TRANSFER") {
                                    return [
                                      "โอนเงิน",
                                      bankDisplayName,
                                      bankSetting?.account_number,
                                    ]
                                      .filter(Boolean)
                                      .join("  •  ");
                                  }
                                  return "";
                                },
                              }}
                            >
                              <MenuItem value="TRANSFER">
                                {bankSetting ? (
                                  <div className="flex items-center gap-2">
                                    {bankSetting.bank?.image_path ? (
                                      <img
                                        src={bankSetting.bank.image_path}
                                        alt={bankSetting.bank?.name}
                                        style={{ width: 20, height: 20, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                                      />
                                    ) : (
                                      <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#e0e0e0", flexShrink: 0 }} />
                                    )}
                                    <div>
                                      <div style={{ color: "#2C2C2C", lineHeight: 1.4 }}>
                                        {bankDisplayName ?? "โอนเงิน"}
                                      </div>
                                      <div style={{ fontSize: 12, color: "#2C2C2C", lineHeight: 1.3 }}>
                                        {bankSetting.account_number}
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  "โอนเงิน"
                                )}
                              </MenuItem>
                              <MenuItem value="CASH">เงินสด</MenuItem>
                            </TextField>
                          </div>
                          <div className="col-span-2">
                            <TextField
                              fullWidth
                              size="small"
                              label="จำนวนเงิน (บาท)"
                              type="number"
                              name={`splits[${index}].amount`}
                              value={split.amount}
                              onChange={handleChange}
                              onBlur={() => handleSplitAmountBlur(index)}
                              error={errors.splits?.[index]?.amount != null}
                              helperText={errors.splits?.[index]?.amount}
                              required
                            />
                          </div>
                          <div className="col-span-1">
                            <TextField
                              fullWidth
                              size="small"
                              label="หมายเหตุ"
                              name={`splits[${index}].remark`}
                              value={split.remark}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="col-span-1 flex items-center">
                            {index > 0 && (
                              <IconButton
                                size="small"
                                onClick={() => arrayHelpers.remove(index)}
                              >
                                <DeleteIcon className="!text-[#FF000B]" />
                              </IconButton>
                            )}
                          </div>
                        </div>
                      ))}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          arrayHelpers.push({
                            channel: "",
                            amount: 0,
                            remark: "",
                          })
                        }
                      >
                        + เพิ่มช่องทางชำระเงิน
                      </Button>
                    </div>
                  )}
                </FieldArray>
              )}

              <hr className="my-3" />

              {/* ── Section 2: รายการปรับปรุง ─────────────────── */}
              <div className="flex items-center mb-2">
                <div className="font-semibold text-base text-[#2DC76D] w-[240px]">
                  ค่าธรรมเนียม / รายการปรับปรุง
                </div>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!values.isShowFee}
                      onChange={(e) =>
                        setFieldValue("isShowFee", e.target.checked)
                      }
                    />
                  }
                  label=""
                />
              </div>
              {values.isShowFee && (
                <FieldArray name="fee_adjustments">
                  {(arrayHelpers) => (
                    <div className="py-2">
                      {values.fee_adjustments.map((fee, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-9 gap-3 mb-3"
                        >
                          <div className="col-span-2">
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="ประเภทปรับปรุง"
                              name={`fee_adjustments[${index}].ar_adjustment_type_id`}
                              value={fee.ar_adjustment_type_id}
                              onChange={handleChange}
                              error={
                                !!errors.fee_adjustments?.[index]
                                  ?.ar_adjustment_type_id
                              }
                              helperText={
                                errors.fee_adjustments?.[index]
                                  ?.ar_adjustment_type_id
                              }
                              required
                            >
                              {adjustmentTypes.map((t) => (
                                <MenuItem key={t.id} value={t.id}>
                                  {t.name}
                                </MenuItem>
                              ))}
                            </TextField>
                          </div>
                          <div className="col-span-2">
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="รายการปรับปรุง"
                              name={`fee_adjustments[${index}].ar_adjustment_item_id`}
                              value={fee.ar_adjustment_item_id}
                              onChange={handleChange}
                            >
                              {adjustmentItems.map((i) => (
                                <MenuItem key={i.id} value={i.id}>
                                  {i.name}
                                </MenuItem>
                              ))}
                            </TextField>
                          </div>
                          <div className="col-span-2">
                            <TextField
                              fullWidth
                              size="small"
                              label="จำนวนเงิน (บาท)"
                              type="number"
                              name={`fee_adjustments[${index}].amount`}
                              value={fee.amount}
                              onChange={handleChange}
                              error={!!errors.fee_adjustments?.[index]?.amount}
                              helperText={
                                errors.fee_adjustments?.[index]?.amount
                              }
                              required
                            />
                          </div>
                          <div className="col-span-2">
                            <TextField
                              fullWidth
                              size="small"
                              label="หมายเหตุ"
                              name={`fee_adjustments[${index}].remark`}
                              value={fee.remark}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="col-span-1 flex items-center">
                            {index > 0 && (
                              <IconButton
                                size="small"
                                onClick={() => arrayHelpers.remove(index)}
                              >
                                <DeleteIcon className="!text-[#FF000B]" />
                              </IconButton>
                            )}
                          </div>
                        </div>
                      ))}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          arrayHelpers.push({
                            ar_adjustment_type_id: "",
                            ar_adjustment_item_id: "",
                            amount: 0,
                            remark: "",
                          })
                        }
                      >
                        + เพิ่มรายการปรับปรุง
                      </Button>
                    </div>
                  )}
                </FieldArray>
              )}

              <hr className="my-3" />

              {/* ── Section 3: ตัดชำระเอกสาร ─────────────────── */}
              <div className="flex items-center mb-2">
                <div className="font-semibold text-base text-[#2DC76D] w-[240px]">
                  ตัดชำระเอกสาร
                </div>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!values.isShowDocument}
                      onChange={(e) =>
                        setFieldValue("isShowDocument", e.target.checked)
                      }
                    />
                  }
                  label=""
                />
              </div>
              {values.isShowDocument && (
                <FieldArray name="document_offsets">
                  {(arrayHelpers) => (
                    <div className="py-2">
                      {values.document_offsets.map((doc, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-9 gap-3 mb-3"
                        >
                          <div className="col-span-2">
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="ประเภทเอกสาร"
                              name={`document_offsets[${index}].offset_document_type`}
                              value={doc.offset_document_type}
                              onChange={handleChange}
                              error={
                                !!errors.document_offsets?.[index]
                                  ?.offset_document_type
                              }
                              helperText={
                                errors.document_offsets?.[index]
                                  ?.offset_document_type
                              }
                              required
                            >
                              {offsetDocumentTypeOptions.map((o) => (
                                <MenuItem key={o.value} value={o.value}>
                                  {o.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </div>
                          <div className="col-span-3">
                            <TextField
                              fullWidth
                              size="small"
                              label="เลขที่เอกสาร"
                              name={`document_offsets[${index}].reference_document_no`}
                              value={doc.reference_document_no}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="col-span-2">
                            <TextField
                              fullWidth
                              size="small"
                              label="จำนวนที่รับชำระ (บาท)"
                              type="number"
                              name={`document_offsets[${index}].amount_applied`}
                              value={doc.amount_applied}
                              onChange={handleChange}
                              error={
                                !!errors.document_offsets?.[index]
                                  ?.amount_applied
                              }
                              helperText={
                                errors.document_offsets?.[index]?.amount_applied
                              }
                              required
                            />
                          </div>
                          <div className="col-span-1">
                            <TextField
                              fullWidth
                              size="small"
                              label="หมายเหตุ"
                              name={`document_offsets[${index}].remark`}
                              value={doc.remark}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="col-span-1 flex items-center">
                            {index > 0 && (
                              <IconButton
                                size="small"
                                onClick={() => arrayHelpers.remove(index)}
                              >
                                <DeleteIcon className="!text-[#FF000B]" />
                              </IconButton>
                            )}
                          </div>
                        </div>
                      ))}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          arrayHelpers.push({
                            offset_document_type: "",
                            reference_document_no: "",
                            amount_applied: 0,
                            remark: "",
                          })
                        }
                      >
                        + เพิ่มเอกสารตัดชำระ
                      </Button>
                    </div>
                  )}
                </FieldArray>
              )}

              <hr className="my-3" />

              {/* ── Section 4: หัก ณ ที่จ่าย ─────────────────── */}
              <div className="flex items-center mb-2">
                <div className="font-semibold text-base text-[#2DC76D] w-[240px]">
                  ถูกหัก ณ ที่จ่าย
                </div>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!values.isShowVat}
                      onChange={(e) => {
                        setFieldValue("isShowVat", e.target.checked);
                        if (!e.target.checked) {
                          setFieldValue("ar_withholding_tax_rate_id", "");
                          setFieldValue("withholding_tax_rate", 0);
                        }
                      }}
                    />
                  }
                  label=""
                />
              </div>
              {values.isShowVat && (
                <div className="py-2">
                  <div className="grid grid-cols-9 gap-3 mb-3">
                    <div className="col-span-2">
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="อัตราภาษีหัก ณ ที่จ่าย"
                        name="ar_withholding_tax_rate_id"
                        value={values.ar_withholding_tax_rate_id}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          setFieldValue(
                            "ar_withholding_tax_rate_id",
                            selectedId,
                          );
                          const found = whtRates.find(
                            (r) => r.id === Number(selectedId),
                          );
                          setFieldValue(
                            "withholding_tax_rate",
                            found ? Number(found.rate) : 0,
                          );
                        }}
                        error={!!errors.ar_withholding_tax_rate_id}
                        helperText={errors.ar_withholding_tax_rate_id}
                        required
                      >
                        {whtRates.map((r) => (
                          <MenuItem key={r.id} value={r.id}>
                            {r.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </div>
                    <div className="col-span-2">
                      <TextField
                        fullWidth
                        size="small"
                        label="มูลค่าถูกหัก ณ ที่จ่าย (บาท)"
                        value={fmt(whtAmount)}
                        InputProps={{ readOnly: true }}
                      />
                    </div>
                  </div>
                </div>
              )}

              <hr className="my-3" />

              {/* ── Summary ───────────────────────────────────── */}
              <div className="flex justify-end items-center mb-2 gap-4">
                <div className="text-[#626477] text-sm space-y-1 text-right">
                  <div>ยอดรับชำระ : {fmt(splitsTotal)} บาท</div>
                  {values.isShowFee && (
                    <div>ปรับปรุงสุทธิ : {fmt(adjustedTotal)} บาท</div>
                  )}
                  {values.isShowDocument && (
                    <div>ตัดชำระเอกสาร : {fmt(documentOffsetsTotal)} บาท</div>
                  )}
                  {values.isShowVat && (
                    <div>ถูกหัก ณ ที่จ่าย : {fmt(whtAmount)} บาท</div>
                  )}
                </div>
                <div className="bg-[#2DC76D] text-white rounded-lg px-4 py-3 text-center min-w-[200px]">
                  <span className="text-sm mr-2">รับชำระสุทธิ</span>
                  <span className="font-bold text-[22px]">{fmt(netPaid)}</span>
                  <span className="ml-1">บาท</span>
                </div>
              </div>
              {values.isShowVat && (
                <div className="flex justify-end items-center mb-2">
                  <span className="text-[#626477] text-sm">
                    รับชำระด้วยเงินรวม :{" "}
                    <span className="text-[#2DC76D] font-medium">
                      {fmt(cashReceived)} บาท
                    </span>
                  </span>
                </div>
              )}
              <div className="flex justify-end items-center mb-3">
                <span className="text-[#626477] text-sm">
                  คงเหลือต้องชำระ :{" "}
                  <span
                    className={
                      remaining < 0 ? "text-red-500" : "text-[#2DC76D]"
                    }
                  >
                    {fmt(remaining)} บาท
                  </span>
                </span>
              </div>

              {/* ── Actions ───────────────────────────────────── */}
              <div className="text-right mt-3">
                <Button
                  variant="outlined"
                  color="gray"
                  className="!px-10"
                  disabled={saving}
                  onClick={() => setModal((prev) => ({ ...prev, open: false }))}
                >
                  ยกเลิก
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className="!px-10 !ml-4"
                  type="submit"
                  disabled={
                    saving ||
                    (!values.isShowSplits &&
                      !values.isShowFee &&
                      !values.isShowDocument &&
                      !values.isShowVat)
                  }
                  onClick={() => handleSubmit()}
                >
                  {saving ? "กำลังบันทึก..." : "ยืนยัน"}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </ModalAllder>
  );
}
